// This is a version of the CoreTabs (core-tabs) component that avoids using the embedded tab content in order to reduce the number
// of DOM elements, and making it easier to size things correctly. This was initially implemented to address height issues on the Study
// Hub.
// Rather than embedding the tabs content within Kendo's TabStripTab component, this version instead renders the tab's content
// alongside. This means that where the original CoreTabs component had 1 component for the whole tab strip, this version will have 2;
// one for the TabStrip and one for the content.

import './styles.scss';
import React, { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { getSessionStorageActivePageTab, setSessionStorageActivePageTab } from '@utils-clean/page-tab-helpers';

interface ITab {
    title: ReactNode;
    content: ReactNode;
    dataTestId: string;
}

interface IProps {
    data: ITab[];
    canChangeTab?: () => Promise<boolean>;
    // This is useful in scenarios where unmounting tab contents causes issues.
    // For example, the add control dialog in RAM splits the form across multiple tabs. Unmounting the tab content
    // causes the form state to be lost making validation more difficult.
    keepTabDataMounted?: boolean;
    onSelectedTabIndexChanged?: (newTabIndex: number) => void;
}

interface ITabContentsProps {
    data: ITab[];
    selectedTabIndex: number;
}

const TabContents = ({ data, selectedTabIndex }: ITabContentsProps) => (
    <>
        {data.map((tab, index) => (
            <div key={`${tab.dataTestId}-content`} style={{ display: index === selectedTabIndex ? 'block' : 'none' }}>
                {tab.content}
            </div>
        ))}
    </>
);

const TabStripLowDom = ({ data, canChangeTab, keepTabDataMounted, onSelectedTabIndexChanged }: IProps) => {
    // This tab uses the session storage to persist the selected tab per page, however this will only work if there
    // is 1 tabstrip on the page. This will not work if we have pages in the future with multiple tabs
    // We store selected tabstrip per page, as it's possible for a page to render its tabstrip before
    // the useEffect in route fires to cleardown the session storage from the previous page.
    const location = useLocation();
    const activePageFromSessionIdx = getSessionStorageActivePageTab(location.key);

    const [selectedTabIndex, setSelectedTabIndex] = React.useState(
        data.length > activePageFromSessionIdx ? activePageFromSessionIdx : 0,
    );

    const selectedTabContent = useMemo(() => data[selectedTabIndex].content, [data, selectedTabIndex]);

    return (
        <>
            <TabStrip
                className="tabstrip-low-dom tabstrip-pages"
                selected={selectedTabIndex}
                onSelect={async (e) => {
                    if (canChangeTab && !(await canChangeTab())) {
                        return;
                    }

                    const newTabIndex = e.selected;
                    onSelectedTabIndexChanged?.(newTabIndex);
                    // uncontrolled: change index locally
                    setSelectedTabIndex(newTabIndex);
                    setSessionStorageActivePageTab(location.key, newTabIndex);
                }}
            >
                {data.map((tab) => (
                    <TabStripTab key={tab.dataTestId} title={<span data-testid={tab.dataTestId}>{tab.title}</span>} />
                ))}
            </TabStrip>
            {keepTabDataMounted ? <TabContents data={data} selectedTabIndex={selectedTabIndex} /> : selectedTabContent}
        </>
    );
};

export default TabStripLowDom;
